import { apiSlice } from "../../../features/api/apiSlice";

/* ------------------------------------------------------------------ */
/* Membership analysis                                                  */
/* ------------------------------------------------------------------ */

/** One breakdown row: how many members, and what that slice paid and owes. */
export type AnalysisRow = {
  key: string | number | null;
  label: string;
  count: number;
  paid: number;
  owed: number;
};

export type MembershipAnalysis = {
  /** Present only when the selection was refused as too large. */
  error?: string;
  code?: string;
  summary: {
    total_members: number;
    living_members: number;
    deceased_members: number;
    new_members: number;
    new_members_year: number;
    members_owing: number;
    members_clear: number;
    generated_at: string;
  };
  /** All amounts already in naira — the server converts from kobo. */
  finance: {
    dues_billed: number;
    dues_paid: number;
    dues_owed: number;
    money_owed: number;
    money_paid: number;
    other_paid: number;
    collection_rate: number;
    members_owing: number;
  };
  finance_by_source: Array<{
    source: string | null; label: string; is_dues: boolean;
    count: number; billed: number; paid: number; balance: number;
  }>;
  by_category: AnalysisRow[];
  by_state: AnalysisRow[];
  by_chapter: AnalysisRow[];
  by_band: AnalysisRow[];
  by_sector: AnalysisRow[];
  by_grade: AnalysisRow[];
  by_gender: AnalysisRow[];
  by_join_year: Array<{ label: string; count: number }>;
  top_debtors: Array<{
    member_id: string; membership_no: string | null; name: string;
    grade: string; owed: number; invoices: number;
  }>;
  participation: {
    members_with_exam_applications: number;
    members_with_event_registrations: number;
  };
  meta: { filters: Record<string, any>; currency: string; unit: string; generated_at: string };
};

export type MembershipAnalysisQuery = {
  state_id?: string | number;
  chapter_id?: string | number;
  category_id?: string | number;
  band_id?: string | number;
  sector_id?: string | number;
  grade?: string;
  gender?: string;
  /** Billing year — scopes the money and the "new members" figure. */
  year?: string | number;
  /** Registration year — scopes the roll itself. */
  reg_year?: string | number;
  exam_id?: string | number;
  event_id?: string | number;
  exclude_deceased?: boolean;
};

export type NamedOption = { id: string | number; name: string };

export type MembershipFilterOptions = {
  states: NamedOption[];
  chapters: NamedOption[];
  categories: NamedOption[];
  bands: NamedOption[];
  sectors: NamedOption[];
  grades: string[];
  genders: string[];
  exams: Array<{ id: string | number; title: string; code?: string }>;
  events: Array<{ id: string | number; title: string; year?: number }>;
  years: number[];
};

export type UserCategory = {
  id: number;
  name: string;
  is_active: boolean;
};

export type MembershipBand = {
  id: number;
  user_category_id: number;
  label: string;
  min_years: number;
  max_years?: number | null;
  discount_percentage: number;
  is_active: boolean;

  category?: UserCategory;

  created_at?: string;
  updated_at?: string;
};

export type MembershipBandPayload = {
  user_category_id: number;
  label: string;
  min_years: number;
  max_years?: number | null;
  discount_percentage?: number;
  is_active?: boolean;
};

export type Paged<T> = {
  data: T[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

function pickArray(res: any) {
  const d = pick(res);
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function pickPage<T>(res: any): Paged<T> {
  const d = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(d)) return { data: d };
  if (Array.isArray(d?.data)) return d;
  return { data: [] };
}

export const membershipApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // =========================
    // Public list for dropdowns
    // GET /membership-bands?user_category_id=&q=&include=category
    // =========================

    getMembers: builder.query({
      query: (params) => ({
        url: "/admin/membership/members",
        params,
      }),
      providesTags: ["Members"],
    }),

    getMemberDetails: builder.query<any, any>({
      query: (id) => `/members/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "Members" as const, id }],
    }),
    // The whole analysis, drawn from one filtered member set. Every filter is
    // optional; nothing set means the entire membership. The server caches each
    // filter combination, so flipping a filter back and forth is free.
    getMembershipDashboard: builder.query<MembershipAnalysis, MembershipAnalysisQuery | void>({
      query: (args) => {
        const p = new URLSearchParams();
        ([
          "state_id", "chapter_id", "category_id", "band_id", "sector_id",
          "grade", "gender", "year", "reg_year", "exam_id", "event_id",
          "exclude_deceased",
        ] as const).forEach((k) => {
          const v = (args as any)?.[k];
          if (v !== undefined && v !== null && v !== false && String(v) !== "") {
            p.set(k, String(v));
          }
        });
        const qs = p.toString();
        return `/admin/membership/dashboard${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["MembershipDashboard"],
    }),

    // Every dropdown the analysis screen needs, in one call.
    getMembershipDashboardFilters: builder.query<MembershipFilterOptions, void>({
      query: () => "/admin/membership/dashboard/filters",
    }),

    // "Log in as" — mint a session token for another user (admin-only, audited).
    impersonateUser: builder.mutation<{ token: string; user: any; impersonated?: boolean }, string | number>({
      query: (userId) => ({ url: `/admin/users/${userId}/impersonate`, method: "POST" }),
    }),

    updateMember: builder.mutation<any, { id: string | number; body: Record<string, any> }>({
      query: ({ id, body }) => ({
        url: `/admin/membership/members/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Members" as const, id: arg.id },
        { type: "Members" as const, id: "LIST" },
      ],
    }),

    /**
     * Who is holding an address that a member's email change was refused for.
     *
     * The refusal names nobody on its own, and almost always it is the SAME
     * PERSON — a member whose recorded address was wrong, who signed up again
     * with their real one. Lazy: only asked for once a save has actually been
     * refused.
     */
    emailHolder: builder.query<any, string>({
      query: (email) => `/admin/membership/email-holder?email=${encodeURIComponent(email)}`,
    }),

    /**
     * Move an address off a login account — and off the member record and any
     * applications attached to it.
     *
     * The only route to an account that never applied and never became a
     * member: it has neither of the other two records to edit.
     */
    updateUserEmail: builder.mutation<any, { userId: number; email: string }>({
      query: ({ userId, email }) => ({
        url: `/admin/membership/users/${userId}/email`,
        method: "POST",
        body: { email },
      }),
      invalidatesTags: ["Members"],
    }),

    /**
     * Delete a duplicate signup — the account, its unpaid application invoice
     * and the files it uploaded. Refused by the server for anything holding a
     * member record, a settled payment or a logbook.
     */
    discardDuplicateAccount: builder.mutation<any, { userId: number }>({
      query: ({ userId }) => ({
        url: `/admin/membership/users/${userId}/discard`,
        method: "POST",
      }),
      invalidatesTags: ["Members"],
    }),

    /**
     * Set a temporary password on a member's account.
     *
     * The server clears `changed_password` alongside it, so the portal's
     * onboarding guard makes the member choose their own at the next sign-in
     * — the admin never learns a password that lasts.
     */
    resetMemberPassword: builder.mutation<
      { message: string; data: { notified: boolean; email: string } },
      { userId: string | number; password: string }
    >({
      query: ({ userId, password }) => ({
        url: `/admin/membership/users/${userId}/password`,
        method: "POST",
        body: { password },
      }),
    }),

    updateMemberEmail: builder.mutation<any, { id: string | number; email: string }>({
      query: ({ id, email }) => ({
        url: `/admin/membership/members/${id}/email`,
        method: "POST",
        body: { email },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Members" as const, id: arg.id },
      ],
    }),

    updateMemberPhone: builder.mutation<any, { id: string | number; phone?: string; phone2?: string }>({
      query: ({ id, ...body }) => ({
        url: `/admin/membership/members/${id}/phone`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Members" as const, id: arg.id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMembersQuery,
  useGetMemberDetailsQuery,
  useImpersonateUserMutation,
  useGetMembershipDashboardQuery,
  useGetMembershipDashboardFiltersQuery,
  useUpdateMemberMutation,
  useLazyEmailHolderQuery,
  useUpdateUserEmailMutation,
  useDiscardDuplicateAccountMutation,
  useResetMemberPasswordMutation,
  useUpdateMemberEmailMutation,
  useUpdateMemberPhoneMutation,
} = membershipApi;
